import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MessageSquare, ArrowRight, Phone, Mail, Clock, ChevronRight,
  HeadphonesIcon, ChevronDown, ChevronUp, Zap,
  Shield, Smartphone, BarChart, MessageCircle, Search, 
  HelpCircle, Activity, Globe, ShieldCheck, Headphones
} from 'lucide-react';
import { BrandLink } from '../components/BrandLogo';
import Seo from '../components/Seo';
import WebsiteFooter from '../components/WebsiteFooter';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { cn } from '../utils/cn';

function Navbar() {
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#ffffff]/80 backdrop-blur-2xl border-b border-[#D8DDE6] px-12 py-6 flex items-center justify-between">
      <BrandLink to="/" size={40} subtitle="Support Desk" />
      <div className="hidden md:flex items-center gap-10">
        {['Features', 'Pricing', 'Contact', 'Support'].map(l => (
          <Link key={l} to={l === 'Support' ? '/support' : l === 'Contact' ? '/contact' : `/#${l.toLowerCase()}`} className="text-[11px] font-bold text-[#706E6B] hover:text-[#080707] uppercase tracking-[0.2em] transition-colors">{l}</Link>
        ))}
      </div>
      <div className="flex items-center gap-6">
        <Link to="/login" className="text-[11px] font-bold text-[#706E6B] hover:text-[#080707] uppercase tracking-widest transition-colors mr-4">Log In</Link>
        <Link to="/register" className="h-12 px-6 rounded-lg bg-[#0176D3] text-white font-bold text-[11px] uppercase tracking-widest flex items-center gap-3 shadow-[0_2px_8px_rgba(0,0,0,0.16)] shadow-blue-600/30 hover:scale-105 transition-all">Get Started <ArrowRight size={14} /></Link>
      </div>
    </nav>
  );
}

const FAQS = [
  { q: 'How do I initialize WaapLeads integration?', a: 'Synchronize your enterprise node by scanning the secure QR signature. Deployment is instantaneous once the handshake is verified.' },
  { q: 'Is there a sandbox environment for testing?', a: 'Yes. We offer a 14-day high-throughput trial with no credit card requirement. Full access to the Pro-tier signal matrix is granted.' },
  { q: 'How many operational nodes can I synchronize?', a: 'Device limits depend on your plan. Starter: 1 device, Pro: 5 devices, Enterprise: unlimited.' },
  { q: 'What are the transmission throughput limits?', a: 'All transmissions are strictly governed by our Anti-Block Logic. Default dispatch rate: 20 signals per minute to ensure node integrity.' },
  { q: 'Are rich-media payloads supported?', a: 'Affirmative. The network supports binary propagation of images, PDFs, videos, and multi-track audio streams across all dispatch campaigns.' },
  { q: 'How does automation work?', a: 'Configure trigger keywords and neural logic templates. Our engine processes incoming signals 24/7, even during node dormancy.' },
  { q: 'What is the security standard for my data?', a: 'We utilize E2EE E2EE logic, JWT session isolation, and API Key shrouding. Our infrastructure is fully GDPR-compliant and encrypted.' },
  { q: 'What payment methodologies are supported?', a: 'We support global credit/debit networks, UPI, and instant digital wallets via Razorpay. System-generated invoices are archived perpetually.' },
  { q: 'Can I decommission my subscription node?', a: 'Decommissioning is available at any time via Node Administration. Signal access remains active until the current billing cycle expires.' },
  { q: 'Is REST API accessibility guaranteed?', a: 'Absolutely. All operational plans include full access to the REST Endpoint Registry for seamless CRM and ERP synchronization.' },
];

const CATEGORIES = [
  { icon: Smartphone, label: 'Node Setup', color: 'text-[#04844B]', bg: 'bg-[#04844B]/10' },
  { icon: MessageCircle, label: 'Batch Signal', color: 'text-[#0176D3]', bg: 'bg-[#0176D3]/10' },
  { icon: Zap, label: 'Automation', color: 'text-[#DD7A01]', bg: 'bg-[#FE9339]/10' },
  { icon: BarChart, label: 'Analytics', color: 'text-[#9050E9]', bg: 'bg-[#9050E9]/10' },
  { icon: ShieldCheck, label: 'Security', color: 'text-[#E3066A]', bg: 'bg-[#E3066A]/10' },
  { icon: MessageSquare, label: 'Billing/Sync', color: 'text-[#0176D3]', bg: 'bg-[#0176D3]/10' },
];

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={cn("border border-[#D8DDE6] rounded-[2rem] overflow-hidden transition-all duration-500 mb-4 group cursor-pointer", open ? "bg-[#ffffff] shadow-4xl border-[#0176D3]/20" : "bg-[#ffffff]/40 hover:bg-[#ffffff]")} onClick={() => setOpen(!open)}>
      <div className="px-8 py-6 flex items-center justify-between gap-6">
        <span className={cn("text-[14px] font-bold uppercase tracking-tight transition-colors", open ? "text-white" : "text-[#706E6B] group-hover:text-[#706E6B] font-mono")}>{q}</span>
        <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center transition-all", open ? "bg-[#0176D3] text-white" : "bg-white/5 text-[#080707]")}>
           {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
      </div>
      {open && (
        <div className="px-10 pb-8 animate-nebula-fade-up">
           <p className="text-[13px] font-bold text-[#706E6B] uppercase tracking-widest leading-relaxed font-mono">{a}</p>
        </div>
      )}
    </div>
  );
}

export default function WebSupportPage() {
  const [search, setSearch] = useState('');
  const filtered = search.trim()
    ? FAQS.filter(f => f.q.toLowerCase().includes(search.toLowerCase()) || f.a.toLowerCase().includes(search.toLowerCase()))
    : FAQS;

  return (
    <div className="min-h-screen bg-[#06080F] text-white selection:bg-[#0176D3] selection:text-white">
      <Seo
        title="Support Center | WaapLeads CRM"
        description="Browse WaapLeads CRM support articles, onboarding help, billing answers, WhatsApp automation guidance, and contact channels."
        pathname="/support"
      />
      <Navbar />

      <main className="pt-40">
        {/* Support Gateway Hero */}
        <section className="px-10 xl:px-32 mb-40 text-center relative overflow-hidden py-24">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-[radial-gradient(circle_at_50%_0%,rgba(37,99,235,0.1),transparent_70%)] pointer-events-none" />
          <div className="max-w-4xl mx-auto space-y-12 relative z-10 animate-nebula-fade-up">
             <div className="w-24 h-24 rounded-[2.5rem] bg-[#0176D3]/10 border border-[#0176D3]/20 flex items-center justify-center text-[#0176D3] mx-auto shadow-4xl ring-4 ring-white/5 group hover:rotate-12 transition-transform duration-1000">
                <Headphones size={48} strokeWidth={1} />
             </div>
             <div className="space-y-6">
                <h1 className="text-6xl font-bold text-[#080707] uppercase tracking-tighter leading-none">Support</h1>
                <p className="text-[13px] font-bold text-[#080707] uppercase tracking-[0.5em] font-mono leading-none">Access our Intelligence Repository or Browse Categorized Interaction Topics</p>
             </div>
             <div className="relative group max-w-2xl mx-auto">
                <Search size={24} className="absolute left-8 top-1/2 -translate-y-1/2 text-[#080707] group-hover:text-[#0176D3] transition-colors" />
                <input
                  type="text"
                  placeholder="Search help articles…"
                  className="w-full bg-[#ffffff] border border-[#D8DDE6] h-20 pl-20 pr-10 rounded-[2.5rem] text-[#080707] font-bold text-[16px] outline-none focus:ring-8 focus:ring-[#0176D3]/10 transition-all shadow-inner uppercase tracking-widest placeholder:text-[#080707]"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
             </div>
          </div>
        </section>

        {/* Support Channels */}
        <section className="px-10 xl:px-32 mb-48 animate-nebula-fade-up">
           <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              {[
                { icon: Phone, label: 'Voice & Signal Node', val: '+91 73920 80372', time: 'Mon–Sat · 10 AM–7 PM IST', cta: 'Chat on WhatsApp', link: 'https://wa.me/917392080372', color: 'text-[#04844B]' },
                { icon: Mail, label: 'Transmission Support', val: 'info@waapcoders.in', time: 'Resolution within 24 operational hours', cta: 'Send Transmission', link: 'mailto:info@waapcoders.in', color: 'text-[#0176D3]' },
                { icon: Clock, label: 'Availability', val: 'Monday – Saturday', time: '10:00 AM – 7:00 PM IST', cta: 'Access Contact Form', link: '/contact', color: 'text-[#DD7A01]' }
              ].map((c, i) => (
                <Card key={i} className="p-12 bg-[#ffffff] border-[#D8DDE6] group hover:border-[#0176D3]/30 transition-all duration-700 rounded-[3rem] shadow-4xl text-left">
                   <div className={cn("w-16 h-16 rounded-lg bg-white/5 border border-[#D8DDE6] flex items-center justify-center mb-10 group-hover:rotate-12 transition-transform", c.color)}>
                      <c.icon size={26} strokeWidth={1.5} />
                   </div>
                   <div className="space-y-6">
                      <div className="space-y-2">
                        <label className="text-[10px] font-bold text-[#080707] uppercase tracking-widest font-mono block">{c.label}</label>
                        <a href={c.link} className="text-xl font-bold text-[#080707] uppercase tracking-tight block hover:text-[#0176D3] transition-colors">{c.val}</a>
                      </div>
                      <p className="text-[11px] font-bold text-[#080707] uppercase tracking-widest font-mono">{c.time}</p>
                      <a href={c.link} className="flex items-center gap-3 text-[10px] font-bold text-[#0176D3] hover:text-[#080707] uppercase tracking-widest transition-colors font-mono pt-4 group/cta">
                        {c.cta} <ArrowRight size={14} className="group-hover/cta:translate-x-1 transition-transform" />
                      </a>
                   </div>
                </Card>
              ))}
           </div>
        </section>

        {/* Categorized Topic Matrix */}
        <section className="px-10 xl:px-32 mb-48 text-center animate-nebula-fade-up">
           <div className="space-y-4 mb-16">
              <span className="text-[11px] font-bold text-[#0176D3] uppercase tracking-[0.5em] font-mono">Structured Intelligence Access</span>
              <h2 className="text-4xl font-bold text-[#080707] uppercase tracking-tighter">Browse by Topic</h2>
           </div>
           <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
              {CATEGORIES.map(({ icon: Icon, label, color, bg }) => (
                <button key={label} className="flex flex-col items-center justify-center gap-6 p-10 rounded-[2.5rem] bg-[#ffffff] border border-[#D8DDE6] shadow-inner group hover:bg-[#ffffff] hover:border-[#0176D3]/20 transition-all duration-500" onClick={() => setSearch(label.split(' ')[0])}>
                   <div className={cn("w-16 h-16 rounded-lg flex items-center justify-center transition-transform duration-700 group-hover:rotate-12", bg, color)}>
                      <Icon size={28} strokeWidth={1.5} />
                   </div>
                   <span className="text-[10px] font-bold text-[#080707] uppercase tracking-widest group-hover:text-[#080707] transition-colors">{label}</span>
                </button>
              ))}
           </div>
        </section>

        {/* FAQ Repository */}
        <section className="px-10 xl:px-32 mb-60 max-w-5xl mx-auto animate-nebula-fade-up">
           <div className="text-center space-y-4 mb-16 px-4">
              <span className="text-[11px] font-bold text-[#0176D3] uppercase tracking-[0.5em] font-mono">System Knowledge Base</span>
              <h2 className="text-4xl font-bold text-[#080707] uppercase tracking-tighter">FAQ Repository</h2>
              <p className="text-[10px] font-bold text-[#080707] uppercase tracking-widest font-mono">
                {search ? `Filtering ${filtered.length} discrepancy record${filtered.length !== 1 ? 's' : ''} for "${search}"` : `${FAQS.length} core queries resolved in the matrix`}
              </p>
           </div>
           <div>
              {filtered.length === 0 ? (
                <div className="py-32 text-center bg-[#ffffff]/40 rounded-[3rem] border border-[#D8DDE6] border-dashed opacity-30">
                   <HelpCircle size={48} className="mx-auto mb-6 text-[#080707]" />
                   <p className="text-[12px] font-bold text-[#080707] uppercase tracking-widest font-mono">No intelligence records found for "{search}". Try alternate keywords or contact support nodes.</p>
                </div>
              ) : (
                filtered.map((faq, i) => <FAQItem key={i} q={faq.q} a={faq.a} />)
              )}
           </div>
        </section>

        {/* Unresolved Discrepancies CTA */}
        <section className="px-10 xl:px-32 mb-20 animate-nebula-fade-up">
           <Card className="bg-[#0176D3] rounded-[4rem] p-24 text-center relative overflow-hidden border-none shadow-[0_0_100px_rgba(37,99,235,0.4)]">
              <div className="absolute top-0 left-0 w-80 h-80 bg-white/10 blur-[100px] rounded-full -translate-x-1/2 -translate-y-1/2" />
              <div className="absolute bottom-0 right-0 w-64 h-64 bg-[#ffffff]/10 blur-[80px] rounded-full translate-x-1/2 translate-y-1/2" />
              
              <div className="relative z-10 max-w-3xl mx-auto space-y-10">
                 <h2 className="text-5xl font-bold text-[#080707] uppercase tracking-tighter">Unresolved Discrepancies?</h2>
                 <p className="text-[13px] font-bold text-[#0176D3] uppercase tracking-widest leading-relaxed font-mono opacity-80">Our operational support nodes are synchronized for instant assistance. We ensure you maximize the throughput of the WaapLeads intelligence engine.</p>
                 <div className="flex flex-col sm:flex-row items-center justify-center gap-8 pt-6">
                    <Link to="/contact" className="h-18 px-12 rounded-lg bg-white text-[#0176D3] font-bold text-[12px] uppercase tracking-[0.2em] flex items-center gap-4 shadow-4xl hover:scale-105 transition-all">Contact Support <ArrowRight size={18} /></Link>
                    <a href="https://wa.me/917392080372" target="_blank" rel="noreferrer" className="flex items-center gap-3 text-[11px] font-bold text-[#080707] hover:text-[#0176D3] uppercase tracking-widest transition-colors font-mono">
                      Chat on WhatsApp <ChevronRight size={16} />
                    </a>
                 </div>
              </div>
           </Card>
        </section>
      </main>

      <WebsiteFooter />
    </div>
  );
}
